import { S3Client, PutObjectCommand, HeadObjectCommand } from '@aws-sdk/client-s3'
import { S3_CONFIG, validateS3Config } from '../config/s3Config.js'

// Re-export validateS3Config for use in other modules
export { validateS3Config }

const BUCKET_NAME = S3_CONFIG.bucketName

// Create S3 client
const s3Client = new S3Client(S3_CONFIG)

/**
 * Parse CSV file to extract metadata for renaming
 * @param {File} file - The CSV file to parse
 * @returns {Promise<{sensorSn: string, date: string, originalNumber: string}>}
 */
export const parseCSVMetadata = async (file) => {
  try {
    const text = await file.text()
    const lines = text.split('\n').filter(line => line.trim())
    
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header and one data row')
    }
    
    // Parse header to find column indices
    const header = lines[0].split(',')
    const dateIndex = header.findIndex(col => col.trim() === 'DATE')
    const sensorSnIndex = header.findIndex(col => col.trim() === 'SENSOR_SN')
    
    if (dateIndex === -1) {
      throw new Error('DATE column not found in CSV')
    }
    if (sensorSnIndex === -1) {
      throw new Error('SENSOR_SN column not found in CSV')
    }
    
    // Find the last valid data row (skip empty lines and incomplete rows)
    let lastValidRow = null
    let lastValidRowIndex = -1
    
    // Start from the end and work backwards to find the last complete row
    for (let i = lines.length - 1; i >= 1; i--) { // Skip header row
      const row = lines[i].split(',')
      
      if (row.length >= header.length && row[dateIndex]?.trim() && row[sensorSnIndex]?.trim()) {
        lastValidRow = row
        lastValidRowIndex = i
        break
      }
    }
    
    if (!lastValidRow) {
      throw new Error('No valid data rows found in CSV')
    }
    
    const dateStr = lastValidRow[dateIndex]?.trim()
    const sensorSn = lastValidRow[sensorSnIndex]?.trim()
    
    if (!dateStr) {
      throw new Error(`No date found in row ${lastValidRowIndex + 1} of CSV`)
    }
    if (!sensorSn) {
      throw new Error(`No SENSOR_SN found in row ${lastValidRowIndex + 1} of CSV`)
    }
    
    // Parse date (format: 2025-08-13)
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date format: ${dateStr}`)
    }
    
    // Format date as YYYYMMDD
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const formattedDate = `${year}${month}${day}`
    
    // Extract number from original filename (e.g., fulllog_666.csv -> 666)
    const filenameMatch = file.name.match(/(\d+)/)
    const originalNumber = filenameMatch ? filenameMatch[1] : '000'
    
    return {
      sensorSn,
      date: formattedDate,
      originalNumber
    }
    
  } catch (error) {
    console.error('Error parsing CSV metadata:', error)
    throw new Error(`Failed to parse CSV metadata: ${error.message}`)
  }
}

/**
 * Generate new filename based on CSV metadata
 * Format: FalY_RRRRMMDD_XXX.csv
 * @param {File} file - Original file
 * @returns {Promise<string>} New filename
 */
export const generateNewFilename = async (file) => {
  try {
    const metadata = await parseCSVMetadata(file)
    const newFilename = `Fal${metadata.sensorSn}_${metadata.date}_${metadata.originalNumber}.csv`
    return newFilename
  } catch (error) {
    console.warn('Failed to generate new filename, using original:', error.message)
    return file.name
  }
}

/**
 * Check if file exists in S3 and get its size
 * @param {string} key - The S3 object key
 * @returns {Promise<{exists: boolean, size?: number}>}
 */
export const checkFileExists = async (key) => {
  try {
    const command = new HeadObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key
    })
    
    const response = await s3Client.send(command)
    return {
      exists: true,
      size: response.ContentLength
    }
  } catch (error) {
    if (error.name === 'NotFound') {
      return { exists: false }
    }
    throw error
  }
}

/**
 * Upload file to S3 with size comparison logic
 * @param {File} file - The file to upload
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<{uploaded: boolean, reason: string, url?: string}>}
 */
export const uploadFileToS3 = async (file, onProgress = null) => {
  try {
    // Generate new filename based on CSV metadata
    const newFilename = await generateNewFilename(file)
    const key = `uploads/${newFilename}`
    
    // Check if file already exists
    const existingFile = await checkFileExists(key)
    
    if (existingFile.exists) {
      // Compare file sizes
      if (file.size <= existingFile.size) {
        return {
          uploaded: false,
          reason: `File already exists and current file (${formatFileSize(file.size)}) is not larger than existing file (${formatFileSize(existingFile.size)})`
        }
      }
    }
    
    // Upload file
    if (onProgress) {
      onProgress({ stage: 'Uploading to S3...', percentage: 0 })
    }
    
    // Convert file to ArrayBuffer to avoid streaming issues
    const fileBuffer = await file.arrayBuffer()
    
    const command = new PutObjectCommand({
      Bucket: BUCKET_NAME,
      Key: key,
      Body: fileBuffer,
      ContentType: file.type || 'text/csv',
      ACL: 'public-read', // Make files publicly readable
      Metadata: {
        originalName: file.name,
        newName: newFilename,
        uploadDate: new Date().toISOString(),
        fileSize: file.size.toString()
      }
    })
    
    // Simulate progress for upload (since S3 doesn't provide real-time progress)
    const progressInterval = setInterval(() => {
      if (onProgress) {
        onProgress({ 
          stage: 'Uploading to S3...', 
          percentage: Math.min(95, Math.random() * 50 + 45) 
        })
      }
    }, 200)
    
    const result = await s3Client.send(command)
    
    clearInterval(progressInterval)
    
    if (onProgress) {
      onProgress({ stage: 'Upload complete!', percentage: 100 })
    }
    
    const fileUrl = `https://${BUCKET_NAME}.fra1.digitaloceanspaces.com/${key}`
    
    return {
      uploaded: true,
      reason: existingFile.exists ? 
        `File overwritten (new size: ${formatFileSize(file.size)}, old size: ${formatFileSize(existingFile.size)})` :
        `File uploaded successfully (${formatFileSize(file.size)})`,
      url: fileUrl,
      key: key,
      originalName: file.name,
      newName: newFilename
    }
    
  } catch (error) {
    console.error('S3 upload error:', error)
    
    // Handle CORS errors specifically
    if (error.message.includes('CORS') || error.message.includes('Failed to fetch')) {
      throw new Error(`CORS error: Please configure CORS settings for your DigitalOcean Spaces bucket. See docs/CORS_SETUP.md for instructions.`)
    }
    
    throw new Error(`Failed to upload file: ${error.message}`)
  }
}

/**
 * Upload multiple files to S3
 * @param {File[]} files - Array of files to upload
 * @param {Function} onProgress - Progress callback function
 * @returns {Promise<Array>} Array of upload results
 */
export const uploadFilesToS3 = async (files, onProgress = null) => {
  const results = []
  const totalFiles = files.length
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i]
    
    if (onProgress) {
      onProgress({
        stage: `Uploading ${file.name} (${i + 1}/${totalFiles})...`,
        percentage: Math.round((i / totalFiles) * 100)
      })
    }
    
    try {
      const result = await uploadFileToS3(file, (progress) => {
        if (onProgress) {
          onProgress({
            ...progress,
            currentFile: file.name,
            fileIndex: i + 1,
            totalFiles: totalFiles
          })
        }
      })
      
      results.push({
        fileName: file.name,
        newFileName: result.newName || file.name,
        ...result
      })
      
    } catch (error) {
      results.push({
        fileName: file.name,
        uploaded: false,
        reason: `Upload failed: ${error.message}`
      })
    }
  }
  
  if (onProgress) {
    onProgress({
      stage: 'All uploads complete!',
      percentage: 100
    })
  }
  
  return results
}

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * Get public URL for a file in S3
 * @param {string} key - The S3 object key
 * @returns {string} Public URL
 */
export const getFileUrl = (key) => {
  return `https://${BUCKET_NAME}.fra1.digitaloceanspaces.com/${key}`
}
